import {
  Component,
  Inject,
  TemplateRef,
  Input,
  EventEmitter,
  Output,
  inject,
  DestroyRef,
  HostListener,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from, noop, of } from 'rxjs';
import { catchError, filter, finalize, map } from 'rxjs/operators';

import { ListProperty, ListAttachment, ListItem, ILegacyList } from '@site-mate/dashpivot-shared-library';

import { WINDOW } from 'app/shared/factory/window.factory';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { FileUploaderModalService } from 'app/shared/service/file-uploader-modal.service';
import { ResourcesService } from 'app/shared/service/resources.service';
import { environment } from 'environments/environment';

import { ListPropertyService } from '../../../list-property.service';
import { IListPropertyCell } from '../list-cell.interface';
import { ListPropertyCellService } from '../list-property-cell.service';

export interface ListAttachmentProperty extends ListProperty {
  value?: ListAttachment[];
}

@Component({
  templateUrl: './list-field-attachment.component.html',
  styleUrls: ['./list-field-attachment.component.scss'],
  selector: 'cc-list-field-attachment',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListFieldAttachmentComponent implements IListPropertyCell {
  @Input() item: ListItem;
  @Input() property: ListAttachmentProperty;
  @Input() listId: string;
  @Input() companyId: string;
  @Input() projectId: string;
  @Output() readonly loadPlaceholder = new EventEmitter<void>();
  @Output() readonly propertyUpdated = new EventEmitter<Record<string, unknown>>();
  readonly state = {
    selectedAttachment: null as ListAttachment,
    attachments: [] as ListAttachment[],
    downloading: false,
  };
  uploadUrl = '';

  private destroyRef = inject(DestroyRef);

  @ViewChild('focusableElement') focusableElement: ElementRef<HTMLDivElement>;
  constructor(
    private readonly confirmService: ConfirmService,
    private readonly errorHandler: ErrorHandler,
    private readonly ngbModal: NgbModal,
    private readonly fileUploaderModalService: FileUploaderModalService,
    private readonly resourcesService: ResourcesService,
    private readonly listPropertyService: ListPropertyService,
    @Inject(WINDOW) private readonly window: Window,
    private readonly listPropertyCellService: ListPropertyCellService,
  ) {}

  ngOnInit() {
    this.listenForUploads();
    this.setAttachments();
    this.setUploadURL();
    this.monitorExternalClicks();
    if (!this.property.value || this.property.value.length === 0) {
      this.addAttachment();
    }
  }

  ngAfterViewInit() {
    this.focusableElement.nativeElement.focus();
  }

  private monitorExternalClicks() {
    const cellId = `${this.property._id}/${this.item._id}`;
    this.listPropertyCellService.setActiveCell(cellId);
    this.listPropertyCellService.$activeCell
      .pipe(
        filter((id) => id !== cellId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadPlaceholder.emit());
  }

  private listenForUploads() {
    this.fileUploaderModalService.allUploadsFinished$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data) => data.url === this.uploadUrl),
        map(({ data }) => {
          const list = data as ILegacyList;
          const allProperties = list.items
            .filter((item) => item.id === this.item.id)
            .reduce((carry, item) => carry.concat(item.properties), [] as ListProperty[]);
          return allProperties.find((property) => property.id === this.property.id);
        }),
        filter((property) => !!property),
      )
      .subscribe((result) => {
        this.property.value = result.value as ListAttachment[];
        this.setAttachments();
      });
  }

  private setUploadURL() {
    const prefix = this.companyId ? 'companies' : 'projects';
    const uploadUrlBase = `${environment.apiUrl}${prefix}/${this.companyId ?? this.projectId}`;

    this.uploadUrl = this.listPropertyService.getAddAttachmentToCellUrl(
      uploadUrlBase,
      this.listId,
      this.item.id,
      this.property.id,
    );
  }

  onPropertyUpdated({ value }: Record<string, ListAttachment[]>) {
    this.property.value = value;
    this.propertyUpdated.emit({ value });
  }

  private setAttachments() {
    this.state.attachments = (this.property.value ?? []).slice();
  }

  addAttachment() {
    this.fileUploaderModalService.add({
      url: this.uploadUrl,
      acceptedFiles: 'application/pdf',
      maxFileSize: 50,
    });
  }

  viewAttachment(templateRef: TemplateRef<any>, attachment: ListAttachment) {
    this.state.selectedAttachment = attachment;
    this.state.attachments = this.property.value as ListAttachment[];
    this.ngbModal.open(templateRef, {
      windowClass: 'attachment-modal',
    });
  }

  downloadAttachment(attachment: ListAttachment) {
    this.state.downloading = true;
    this.resourcesService
      .getDownloadUrl(attachment.fullUrl, { keyName: attachment.filename })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.errorHandler.handle(error);
          return of(null);
        }),
        finalize(() => {
          this.state.downloading = false;
        }),
      )
      .subscribe((downloadUrl) => {
        this.window.location.href = downloadUrl;
      });
  }

  deleteAttachment(attachment: ListAttachment) {
    from(this.confirmService.confirmDelete('deleteListItemPropertyAttachment')).subscribe({
      next: () => {
        const values = this.state.attachments.slice();
        const updatedValue = values.filter((entry) => entry.fullUrl !== attachment.fullUrl);
        this.property.value = updatedValue;
        this.setAttachments();
        this.onPropertyUpdated({ value: updatedValue });
      },
      error: noop,
    });
  }

  @HostListener('document:click', ['$event'])
  onFieldClick(event: MouseEvent) {
    const allowedFields = [
      'cc-list-field-attachment',
      'cc-file-uploader-modal',
      '.dz-hidden-input',
      '.swal2-container',
    ].join(', ');

    const isAttachmentField = (event.target as HTMLElement).closest(allowedFields) !== null;

    if (!isAttachmentField) {
      this.loadPlaceholder.emit();
    }
  }
}
